/**
 * Agenda Task Service
 * 
 * Service for fetching and formatting priority tasks from the Agenda
 * for display in the Focus Mode blocked overlay.
 */

import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { AgendaTask, TaskPriority } from '../types/agenda.types';

// Priority weights for sorting (higher = more urgent)
const PRIORITY_WEIGHTS: Record<TaskPriority, number> = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1
};

/**
 * Fetch top 3 priority tasks for a user
 * Tasks are sorted by: Priority (urgent first) → Deadline → Created date
 * 
 * @param userId - User ID to fetch tasks for
 * @returns Array of top 3 priority tasks
 */
export async function getTopPriorityTasks(userId: string): Promise<AgendaTask[]> {
  try {
    // Query incomplete tasks for this user
    const tasksRef = collection(db, 'agenda_tasks');
    const q = query(
      tasksRef,
      where('userId', '==', userId),
      where('completed', '==', false)
    );
    
    const snapshot = await getDocs(q);
    const tasks: AgendaTask[] = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      tasks.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        completedAt: data.completedAt?.toDate() || null,
        deadline: data.deadline?.toDate(),
        timerStartedAt: data.timerStartedAt?.toDate() || null
      } as AgendaTask);
    });
    
    // Sort by priority, then deadline, then created date
    const sorted = tasks.sort((a, b) => {
      // 1. Priority first (urgent → high → medium → low)
      const priorityA = PRIORITY_WEIGHTS[a.priority || 'medium'];
      const priorityB = PRIORITY_WEIGHTS[b.priority || 'medium'];
      
      if (priorityA !== priorityB) {
        return priorityB - priorityA; // Higher priority first
      }
      
      // 2. Deadline (earlier deadlines first)
      if (a.deadline && b.deadline) {
        return a.deadline.getTime() - b.deadline.getTime();
      }
      if (a.deadline) return -1; // Tasks with deadlines come before those without
      if (b.deadline) return 1;
      
      // 3. Created date (older tasks first)
      return a.createdAt.getTime() - b.createdAt.getTime();
    });
    
    // Return top 3
    return sorted.slice(0, 3);
  } catch (error) {
    console.error('❌ Error fetching priority tasks:', error);
    return [];
  }
}

/**
 * Format priority for display
 * 
 * @param priority - Task priority level
 * @returns Formatted priority string
 */
export function formatPriority(priority?: TaskPriority): string {
  switch (priority) {
    case 'urgent':
      return 'Urgent';
    case 'high':
      return 'Hoog';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Laag';
    default:
      return 'Geen prioriteit';
  }
}

/**
 * Format deadline for display in Dutch
 * Shows relative time for near deadlines, absolute date for far ones
 * 
 * @param deadline - Task deadline date
 * @returns Formatted deadline string
 */
export function formatDeadline(deadline?: Date): string {
  if (!deadline) return '';
  
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const taskDate = new Date(deadline.getFullYear(), deadline.getMonth(), deadline.getDate());
  
  const diffDays = Math.floor((taskDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'deadline: vandaag';
  if (diffDays === 1) return 'deadline: morgen';
  if (diffDays === -1) return 'deadline: gisteren (te laat!)';
  if (diffDays < 0) return `deadline: ${Math.abs(diffDays)} dagen geleden`;
  if (diffDays <= 7) return `deadline: over ${diffDays} dagen`;
  
  // For far deadlines, show the actual date
  return `deadline: ${deadline.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long'
  })}`;
}
